import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./SpotlightCarousal.module.scss";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import dayjs from "dayjs";
import UpcomingAppointmentCard from "./UpcomingAppointmentCard";
import { useGetUpcomingAppointmentsQuery } from "../services/GetUpcommingAppointmentsApiSlice";
import DataLoader from "../../../components/custom-data-loader/DataLoader";
import SpotlightCarousalSkeleton from "./SpotlightCarousalSkeleton";
import KnowledgeCard from "../../../components/knowledge-cards/KnowledgeCard";
import { useGetSpotlightAgilityContentQuery } from "../services/GetSpotlightAgilityContentApiSlice";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Pagination, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useLocation, useNavigate } from "react-router-dom";
import { useSaveContentClickMutation } from "../../view-learning-content/services/SaveContentStatusAccessLogAPISlice";
import { setLearningContent } from "../../view-learning-content/services/LearningContentDataSlice";
import {
  CHILDLIVINGSTATUSATBIRTH_STATUS_ID_MAPPING,
  SAVE_CLICK_STATUS_ID,
} from "../../../utility/constants/common-constants";
import {
  getContentDuration,
  getPatientRoute,
} from "../../../utility/functions/common-functions";
import { setIsSpotlightVisible } from "../../../pages/opt-in-opt-out/services/PatientStatusSlice";

type PatientAppointment = {
  appointmentId: string;
  patientProfileId: number;
  patientAppointmentCsnid: number;
  visitTypeId: number;
  visitTypeName: string;
  appointmentDate: string;
  scheduledResourceId: number;
  npi: number;
  scheduledResourceName: string;
  clinicianTitle: string | null;
  departmentId: number;
  departmentName: string;
  appointmentStatus: string;
  pregnancyEpisodeId: string;
  isReminderSent: boolean | null;
  physicianHeadshot: string;
};

type AppointmentGroup = {
  reminderDay: number;
  patientAppointments: PatientAppointment[];
};

const SpotlightCarousel: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const { patientProfileId } = useAppSelector(
    (state) => state.patientOnboardingStatus
  );

  const { livingStatusAtBirthId } = useAppSelector(
    (state) => state.patientOnboardingStatus.loginWorkflowInfo
  );

  const preferredLanguage = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );

  const patientProfileInfo = useAppSelector(
    (state) => state.patientProfileDetails.patientProfileInfo
  );

  const shouldShowLearningWidgets =
    livingStatusAtBirthId ===
      CHILDLIVINGSTATUSATBIRTH_STATUS_ID_MAPPING.living ||
    livingStatusAtBirthId === null;

  const [saveContentClick] = useSaveContentClickMutation();

  const {
    data: upcomingAppointmensData,
    isLoading: upcomingAppointmensLoading,
    error: upcomingAppointmensError,
    refetch,
  } = useGetUpcomingAppointmentsQuery({ patientProfileId, preferredLanguage });

  const {
    data: agilityContentData,
    isLoading: agilityContentLoading,
    error: agilityContentError,
  } = useGetSpotlightAgilityContentQuery(
    {
      patientProfileId,
      preferredLanguage,
    },
    {
      skip: !shouldShowLearningWidgets,
    }
  );

  const upcomingAppointments: PatientAppointment[] = useMemo(() => {
    return (
      upcomingAppointmensData?.upcomingAppointments?.flatMap(
        (group: AppointmentGroup) => group.patientAppointments
      ) || []
    );
  }, [upcomingAppointmensData]);

  // --- NEW: count slides & detect single + mobile
  const numSlides =
    upcomingAppointments.length +
    (agilityContentData?.additionalKnowledgeContents?.length || 0);
  const isSingle = numSlides === 1;

  useEffect(() => {
    if (
      !shouldShowLearningWidgets &&
      upcomingAppointmensData?.upcomingAppointments.length === 0
    ) {
      dispatch(setIsSpotlightVisible(false));
      return;
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const now = dayjs();
    const nextTimeout = upcomingAppointments
      .map((appt) => {
        const expiry = dayjs(appt.appointmentDate).add(30, "minute");
        const diff = expiry.diff(now);
        return diff;
      })
      .filter((ms) => ms > 0)
      .sort((a, b) => a - b)[0];
    if (nextTimeout) {
      timeoutRef.current = setTimeout(() => {
        refetch();
      }, nextTimeout);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [upcomingAppointments, refetch]);

  const handleKnowledgeCardClick = async (content: any) => {
    const publishedDate = dayjs(content.publishDate);

    dispatch(
      setLearningContent({
        type: content.educationalContentType,
        category: content.activitySubCategoryName || "",
        isNew: content.isNew,
        fullHtml: content.body,
        title: content.title,
        summarry: content.summary,
        patientProfileId: patientProfileId,
        lang: preferredLanguage,
        length: getContentDuration(content.length, content.videoDuration),
        audioUrl: content.audio?.url || "",
        thumbnail: content.image?.url || "",
        videoUrl: content?.video?.url,
        publisherName:
          `${content.articleAuthor.name}, ${content.articleAuthor.credentials}` ||
          "",
        publishedDate:
          `${t("viewLearningContent.published", {
            date: publishedDate.format("MMM D, YYYY"),
          })}` || "",
        navigationInfo: {
          isViewLearningContentEnabled: true,
          arrivedFrom: location.pathname,
        },
      })
    );

    try {
      await saveContentClick({
        body: {
          casePlanMilestoneMappingId: "",
          patientProfileId,
          caseId: 1,
          planId: 1,
          milestoneId: 1,
          contentIdentifier: content.contentId.toString(),
          contentWatchedDurationInMins: 1,
          educationalContentSourceId: content.educationalContentSourceId,
          action: SAVE_CLICK_STATUS_ID,
        },
        patientPreferredLang: preferredLanguage,
      }).unwrap();
    } catch (err) {
      console.error("saveContentClick error", err);
    }
    navigate(getPatientRoute(patientProfileId, "/learning"), {
      state: { selectedTabId: 5 },
    });
  };

  return (
    <DataLoader
      isLoading={upcomingAppointmensLoading || agilityContentLoading}
      error={upcomingAppointmensError && agilityContentError}
      data={upcomingAppointmensData || agilityContentData}
      loadingComponent={<SpotlightCarousalSkeleton />}
      errorComponent={
        <Typography color="error">Error Loading Appointment Data</Typography>
      }
      emptyComponent={<Typography>No data found.</Typography>}
    >
      {() =>
        (shouldShowLearningWidgets ||
          upcomingAppointmensData?.upcomingAppointments.length > 0) && (
          <Box className={styles.spotlightCarousal}>
            <Box className={styles.spotlightCarouselContainer}>
              {/* Greetings */}
              <Box className={styles.greetings}>
                <Typography
                  variant="bodySmall"
                  color="common.black"
                  sx={{
                    fontWeight: "700",
                    textTransform: "uppercase",
                    opacity: "80%",
                    letterSpacing: "0.72px",
                  }}
                >
                  {t("hello")}{" "}
                  {patientProfileInfo.userName ||
                    patientProfileInfo.name?.split(" ")[0] ||
                    ""}
                </Typography>
                <Typography
                  variant="h1"
                  fontSize={{ xs: "24px", md: "40px" }}
                >
                  {t("spotLightCarousel.forYouToday")}
                </Typography>
              </Box>

              {/* Carousel */}
              <Box
                className={
                  `${styles.carouselWrapper}` +
                  (isAtEnd ? ` ${styles.noLeftMargin}` : "") +
                  (isSingle && isMobile ? ` ${styles.onlyOneMobile}` : "")
                }
              >
                <Swiper
                  modules={[Pagination, Thumbs]}
                  pagination={{
                    el: `.${styles.customSwiperPagination}`,
                    clickable: true,
                  }}
                  slidesPerView={2.3}
                  spaceBetween={2}
                  centeredSlides={false}
                  centerInsufficientSlides={true}
                  breakpoints={{
                    0: { slidesPerView: 1.1, spaceBetween: 12 },
                    400: { slidesPerView: 1.15, spaceBetween: 4 },
                    450: { slidesPerView: 1.7, spaceBetween: 4 },
                    600: { slidesPerView: 2, spaceBetween: 4 },
                    900: { slidesPerView: 2.4, spaceBetween: 8 },
                  }}
                  onSlideChange={(swiper: SwiperType) =>
                    setIsAtEnd(swiper.isEnd)
                  }
                >
                  {[
                    ...upcomingAppointments,
                    ...(agilityContentData?.additionalKnowledgeContents ?? []),
                  ].map((item, index) => {
                    if ("appointmentId" in item) {
                      const date = dayjs(item.appointmentDate);
                      return (
                        <SwiperSlide key={item.appointmentId}>
                          <UpcomingAppointmentCard
                            physicianName={item.scheduledResourceName}
                            specialty={item.departmentName}
                            healthSystem="Temple Health"
                            appointmentDate={date.format("ddd, MMM D")}
                            appointmentTime={date.format("h:mm A")}
                            providerFhirId={item.providerFhirId || ""}
                          />
                        </SwiperSlide>
                      );
                    }

                    // KnowledgeCard fallback
                    const duration = getContentDuration(
                      item.length,
                      item.videoDuration
                    );
                    const videoDuration =
                      duration === 0
                        ? ""
                        : item.videoDuration != null &&
                          item.videoDuration < 1 &&
                          duration === item.videoDuration
                        ? ` | ${t("educationContent.lessThanAMin")}`
                        : ` | ${duration} ${t("educationContent.min")}`;
                    return (
                      <SwiperSlide key={item.contentID ?? index}>
                        <KnowledgeCard
                          contentType={item.educationalContentType}
                          imageSrc={item.image?.url ?? ""}
                          imageAlt={item.image?.label ?? ""}
                          title={item.title}
                          isNew={item.isNew}
                          length={videoDuration}
                          onClick={() => handleKnowledgeCardClick(item)}
                          isAudio={!!item.audio.url}
                        />
                      </SwiperSlide>
                    );
                  })}
                </Swiper>

                <Box className={styles.customSwiperPagination} />
              </Box>
            </Box>
          </Box>
        )
      }
    </DataLoader>
  );
};

export default SpotlightCarousel;
